import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Avatar, Drawer, Dropdown, Layout, Badge, Grid } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  HeartOutlined,
  LogoutOutlined,
  ReadOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
import {
  useActiveCart,
  useClearCart,
  useRemoveCartLine,
  useUpdateCartLine,
} from '@core/hooks/useCart';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useUiStore } from '@core/store/uiStore';
import { useWishlistStore } from '@core/store/wishlistStore';
import { getAvatarStyles } from '@core/utils/avatar';
import { getChargeRows } from '@core/utils/charges';
import { useStoreClosed } from '@core/utils/storeStatus';
import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import Button from '@components/base/Button';
import CartLineDetails from '@components/base/CartLineDetails';
import {
  CLOSE_CHECKOUT_MODAL,
  OPEN_CHECKOUT_MODAL,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import ImageWithFallback from '@components/base/ImageWithFallback';
import MobileNav from '@components/base/MobileNav';
import Price from '@components/base/Price';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import ProductDetailView from '@features/product-detail/ProductDetailView.jsx';
import AppModal from '@components/base/AppModal';
import { useRetainedWhileClosing } from '@components/base/AppModal/useRetainedWhileClosing';
import { useCopy } from '@/app/providers/useCopy';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';

const { Header } = Layout;
const CheckoutPage = lazy(() => import('@features/checkout/CheckoutPage.jsx'));
const DESKTOP_SEARCH_MIN_PX = 224;
const SEARCH_STACK_HYSTERESIS_PX = 40;

export default function HeaderDefault() {
  const t = useCopy();
  const { customer, isAuthenticated, logout } = useAuth();
  const { cart, lineCount } = useActiveCart();
  const clearCart = useClearCart();
  const updateCartLine = useUpdateCartLine();
  const removeCartLine = useRemoveCartLine();
  const { checkStoreOpenOrShowModal } = useStoreClosed();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const { language } = useAppLanguage();
  const location = useLocation();
  const { pathname, search } = location;
  const isSearchAllowedPage =
    pathname === '/' ||
    pathname === '/catalog' ||
    pathname === '/categories' ||
    pathname.startsWith('/catalog') ||
    pathname.startsWith('/categories');
  const shouldHideHeaderSearch = !isSearchAllowedPage;
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isCartDrawerViewport = Boolean(screens.sm);
  const headerInnerRef = useRef(null);
  const brandGroupRef = useRef(null);
  const actionsRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [editingCartLine, setEditingCartLine] = useState(null);
  const [shownCartLine, releaseCartLine] = useRetainedWhileClosing(editingCartLine);
  const [showAllCartLines, setShowAllCartLines] = useState(false);
  const [isSearchStacked, setIsSearchStacked] = useState(false);
  const [searchValue, setSearchValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  });

  const appName = runtimeBrandName || config?.copy?.appName || config?.name || 'PickBazar';
  const displayName = customer?.first_name || customer?.email?.split('@')[0] || 'Account';
  const activeLocation = locations.find((l) => l.id === locationId) ?? locations[0];

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName, disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    {
      key: 'switch_account',
      icon: <UserSwitchOutlined />,
      label: t('switchAccount', 'Switch Account'),
    },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'switch_account') openAccountSelector();
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };
  const navItems = [
    { label: 'Home', to: '/' },
    { label: 'Shop', to: '/catalog' },
    { label: 'Orders', to: '/orders', auth: true },
  ];
  const cartItems = cart?.items ?? [];
  const isCartEmpty = cartItems.length === 0;
  const isCheckoutDisabled = isCartEmpty || clearCart.isPending;
  const totalItemQuantity = cartItems.reduce((sum, line) => sum + (line.quantity ?? 0), 0);
  const chargeRows = getChargeRows(cart);
  const shouldCollapseCartLines = !isCartDrawerViewport;
  const visibleCartItems =
    shouldCollapseCartLines && !showAllCartLines ? cartItems.slice(0, 4) : cartItems;
  const hasHiddenCartLines = shouldCollapseCartLines && cartItems.length > 4;

  useEffect(() => {
    if (location.state?.showOrderConfirmation) {
      setCheckoutOpen(false);
      setCartOpen(false);
      return;
    }
    if (!location.state?.openCartDrawer && !location.state?.openCheckoutModal) return;
    if (location.state?.openCartDrawer) setCartOpen(true);
    if (location.state?.openCheckoutModal) setCheckoutOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  // Shared open/close checkout events (openCheckoutModal(), and CheckoutPage
  // closing the modal once an order is placed).
  useEffect(() => {
    const handleOpen = () => setCheckoutOpen(true);
    const handleClose = () => setCheckoutOpen(false);
    window.addEventListener(OPEN_CHECKOUT_MODAL, handleOpen);
    window.addEventListener(CLOSE_CHECKOUT_MODAL, handleClose);
    return () => {
      window.removeEventListener(OPEN_CHECKOUT_MODAL, handleOpen);
      window.removeEventListener(CLOSE_CHECKOUT_MODAL, handleClose);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const query = params.get('q') || '';
    setSearchValue(query);
  }, [search]);

  useLayoutEffect(() => {
    if (isMobile) {
      setIsSearchStacked(false);
      return undefined;
    }

    const inner = headerInnerRef.current;
    const brand = brandGroupRef.current;
    const actions = actionsRef.current;
    if (!inner || !brand || !actions) return undefined;

    const update = () => {
      const styles = getComputedStyle(inner);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '16') || 16;
      const available = inner.clientWidth;
      const needed = brand.offsetWidth + actions.offsetWidth + DESKTOP_SEARCH_MIN_PX + gap * 2;

      setIsSearchStacked((prev) => {
        if (prev) return needed + SEARCH_STACK_HYSTERESIS_PX > available;
        return needed > available;
      });
    };

    const observer = new ResizeObserver(update);
    observer.observe(inner);
    observer.observe(brand);
    observer.observe(actions);
    update();

    return () => observer.disconnect();
  }, [
    isMobile,
    language,
    lineCount,
    wishlistCount,
    isAuthenticated,
    runtimeLogoUrl,
    activeLocation?.name,
  ]);

  const openCheckout = () => {
    if (isCheckoutDisabled) return;
    if (!checkStoreOpenOrShowModal()) return;
    setCartOpen(false);
    if (isAuthenticated) {
      setCheckoutOpen(true);
      return;
    }
    setAuthRedirect('__checkout_modal__');
    setAuthMode('login');
  };

  const submitSearch = (e) => {
    e.preventDefault();
    const query = searchValue.trim();
    navigate(query ? `/catalog?q=${encodeURIComponent(query)}` : '/catalog');
  };

  const openOrders = () => {
    if (isAuthenticated) {
      navigate('/orders');
      return;
    }
    setAuthRedirect('/orders');
    setAuthMode('login');
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    const trimmed = value.trim();
    navigate(trimmed ? `/catalog?q=${encodeURIComponent(trimmed)}` : '/catalog');
  };

  const renderSearchForm = ({ className, placeholder }) => (
    <form className={className} onSubmit={submitSearch}>
      <input
        type="search"
        value={searchValue}
        onChange={handleSearchChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submitSearch(e);
          }
        }}
        placeholder={placeholder}
      />
      <button type="submit" className="default-header-search-submit" aria-label="Search">
        <SearchOutlined />
      </button>
    </form>
  );

  return (
    <>
      <Header className="default-header default-header-meatlike">
        <div
          ref={headerInnerRef}
          className={`default-header-inner ${isMobile ? 'is-mobile' : ''} ${
            !isMobile && isSearchStacked ? 'is-search-stacked' : ''
          }`}
        >
          {isMobile ? (
            <>
              <div className="default-header-mobile-row-1">
                <div className="default-header-brand-group">
                  <Link to="/" className="default-brand" aria-label={`${appName} home`}>
                    {runtimeLogoUrl || config?.logoUrl ? (
                      <img
                        src={runtimeLogoUrl || config.logoUrl}
                        alt=""
                        style={{ objectFit: 'contain', background: 'transparent' }}
                      />
                    ) : (
                      <span className="default-brand-mark" aria-hidden>
                        P
                      </span>
                    )}
                  </Link>
                  <button
                    type="button"
                    className="default-header-location default-header-location-mobile"
                    onClick={openStoreSelector}
                    aria-label={t('changeStore', 'Change store')}
                  >
                    <img src="/location-pin.png" alt="Location" className="location-icon-img" />
                    <span>{activeLocation?.name ?? ''}</span>
                  </button>
                </div>

                <div className="default-header-actions">
                  <Link
                    to="/wishlist"
                    className={`default-header-utility default-header-utility-pill ${pathname.startsWith('/wishlist') ? 'is-active' : ''}`}
                    aria-label="Wishlist"
                  >
                    <Badge count={wishlistCount} size="small" offset={[-2, 2]}>
                      <HeartOutlined />
                    </Badge>
                  </Link>
                  {isAuthenticated ? (
                    <Dropdown
                      menu={{ items: accountItems, onClick: onAccountClick }}
                      trigger={['click']}
                    >
                      <button
                        type="button"
                        className="default-header-account default-header-account-pill"
                        aria-label="Account"
                      >
                        <Avatar
                          size={28}
                          style={{
                            ...getAvatarStyles(displayName),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {displayName[0]?.toUpperCase()}
                        </Avatar>
                      </button>
                    </Dropdown>
                  ) : (
                    <button
                      type="button"
                      className="default-header-account default-header-account-pill"
                      onClick={() => setAuthMode('choice')}
                      aria-label="Account"
                    >
                      <UserOutlined />
                    </button>
                  )}
                </div>
              </div>

              <div
                className={`default-header-mobile-row-2 ${shouldHideHeaderSearch ? 'is-hidden' : ''}`}
              >
                {renderSearchForm({
                  className: 'default-header-search is-mobile-search',
                  placeholder: t('searchPlaceholder', 'Search your products'),
                })}
              </div>
            </>
          ) : (
            <>
              <div className="default-header-desktop-row">
                <div ref={brandGroupRef} className="default-header-left-group">
                  <Link to="/" className="default-brand" aria-label={`${appName} home`}>
                    {runtimeLogoUrl || config?.logoUrl ? (
                      <img
                        src={runtimeLogoUrl || config.logoUrl}
                        alt=""
                        style={{ objectFit: 'contain', background: 'transparent' }}
                      />
                    ) : (
                      <span className="default-brand-mark" aria-hidden>
                        {appName?.[0] || 'P'}
                      </span>
                    )}
                    <span className="default-brand-name">{appName}</span>
                  </Link>

                  <button
                    type="button"
                    className="default-header-location"
                    onClick={openStoreSelector}
                    aria-label={t('changeStore', 'Change store')}
                  >
                    <img src="/location-pin.png" alt="Location" className="location-icon-img" />
                    <span>{activeLocation?.name ?? ''}</span>
                  </button>
                  <Link to="/catalog" className="default-header-shop-link">
                    <ShoppingOutlined />
                    <span>{t('shop', 'Shop')}</span>
                  </Link>
                </div>

                {!isSearchStacked &&
                  !shouldHideHeaderSearch &&
                  renderSearchForm({
                    className: 'default-header-search is-centered-search',
                    placeholder: t('searchPlaceholder', 'Search your products'),
                  })}

                <div ref={actionsRef} className="default-header-actions">
                  <Link
                    to="/wishlist"
                    className={`default-header-utility ${pathname.startsWith('/wishlist') ? 'is-active' : ''}`}
                    aria-label="Wishlist"
                  >
                    <Badge count={wishlistCount} size="small">
                      <HeartOutlined />
                    </Badge>
                  </Link>
                  <button
                    type="button"
                    className={`default-header-utility ${pathname.startsWith('/orders') ? 'is-active' : ''}`}
                    onClick={openOrders}
                    aria-label={t('myOrders', 'Orders')}
                  >
                    <ReadOutlined />
                  </button>
                  <button
                    type="button"
                    className="default-header-utility"
                    aria-label="Cart"
                    onClick={() => setCartOpen(true)}
                  >
                    <Badge count={lineCount} size="small">
                      <ShoppingCartOutlined />
                    </Badge>
                  </button>
                  {isAuthenticated ? (
                    <Dropdown
                      menu={{ items: accountItems, onClick: onAccountClick }}
                      trigger={['click']}
                    >
                      <button type="button" className="default-header-account" aria-label="Account">
                        <Avatar
                          size={30}
                          style={{
                            ...getAvatarStyles(displayName),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <span>{displayName[0]?.toUpperCase()}</span>
                        </Avatar>
                      </button>
                    </Dropdown>
                  ) : (
                    <button
                      type="button"
                      className="default-header-account"
                      onClick={() => setAuthMode('choice')}
                      aria-label="Account"
                    >
                      <UserOutlined />
                    </button>
                  )}
                </div>
              </div>
              {isSearchStacked && !shouldHideHeaderSearch && (
                <div className="default-header-desktop-search-row">
                  {renderSearchForm({
                    className: 'default-header-search is-stacked-search',
                    placeholder: t('searchPlaceholder', 'Search your products'),
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </Header>
      <MobileNav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        items={navItems}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {runtimeLogoUrl || config?.logoUrl ? (
              <img
                src={runtimeLogoUrl || config.logoUrl}
                alt=""
                style={{
                  height: '1.75rem',
                  width: 'auto',
                  objectFit: 'contain',
                  background: 'transparent',
                }}
              />
            ) : null}
            <strong style={{ fontSize: '1.05rem', color: 'var(--color-text)' }}>{appName}</strong>
          </div>
        }
        extraContent={
          <div
            className="default-mobile-nav-controls"
            style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
          >
            {!shouldHideHeaderSearch && (
              <form
                className="default-mobile-search-form"
                onSubmit={(e) => {
                  submitSearch(e);
                  setNavOpen(false);
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'var(--bg-surface, #f6f8f3)',
                    borderRadius: '0.375rem',
                    padding: '0.4rem 0.65rem',
                    border: '1px solid var(--border-color, #e5e7eb)',
                  }}
                >
                  <SearchOutlined style={{ color: '#8c8c8c', marginRight: '0.5rem' }} />
                  <input
                    type="search"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder={t('searchPlaceholder', 'Search for products...')}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      width: '100%',
                      fontSize: '0.875rem',
                    }}
                  />
                </div>
              </form>
            )}

            <button
              type="button"
              className="default-header-location"
              onClick={() => {
                openStoreSelector();
                setNavOpen(false);
              }}
            >
              <img src="/location-pin.png" alt="Location" className="location-icon-img" />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeLocation?.name ?? ''}
              </span>
            </button>
          </div>
        }
      />
      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === '__checkout_modal__') {
            setCheckoutOpen(true);
          } else if (authRedirect) {
            navigate(authRedirect);
          }
          setAuthRedirect(null);
        }}
      />
      <Drawer
        className="default-cart-drawer"
        title={
          <span className="default-drawer-title">
            <ShoppingCartOutlined />
            <strong>{t('yourCart', 'Your Cart')}</strong>
            <small>
              ({lineCount} {t('items', 'Items')})
            </small>
          </span>
        }
        extra={
          lineCount > 0 ? (
            <Button
              className="default-drawer-clear-all-button"
              type="text"
              size="small"
              onClick={() => clearCart.mutate()}
              loading={clearCart.isPending}
            >
              {t('clearAll', 'Clear all')}
            </Button>
          ) : null
        }
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        width={isCartDrawerViewport ? 430 : '100%'}
        placement="right"
        maskClosable
        keyboard
      >
        <div className="default-drawer-layout">
          <div className="default-drawer-lines">
            {visibleCartItems.length === 0 ? (
              <div className="default-drawer-empty">
                <ShoppingCartOutlined />
                <strong>{t('yourCartIsEmpty', 'Your cart is empty')}</strong>
                <span>{t('cartEmptyDesc', 'Add products to get started.')}</span>
              </div>
            ) : (
              visibleCartItems.map((line) => (
                <article className="default-drawer-line" key={line.lineId}>
                  <ImageWithFallback
                    src={line.imageUrl}
                    alt={line.itemName}
                    aspectRatio="1 / 1"
                    radius={8}
                  />
                  <div className="default-drawer-line-main">
                    <CartLineDetails line={line} t={t} />
                  </div>
                  <div className="default-drawer-line-actions">
                    {!line.isUpsellingItem && (
                      <button
                        type="button"
                        aria-label={t('edit', 'Edit') + ` ${line.itemName}`}
                        onClick={() => setEditingCartLine(line)}
                      >
                        <EditOutlined />
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label={t('remove', 'Remove') + ` ${line.itemName}`}
                      onClick={() => removeCartLine.mutate(line.lineId)}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                  <div className="default-drawer-stepper">
                    <button
                      type="button"
                      disabled={line.quantity <= 1}
                      onClick={() =>
                        updateCartLine.mutate({
                          lineId: line.lineId,
                          quantity: line.quantity - 1,
                        })
                      }
                    >
                      -
                    </button>
                    <span>{line.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateCartLine.mutate({
                          lineId: line.lineId,
                          quantity: line.quantity + 1,
                        })
                      }
                    >
                      +
                    </button>
                  </div>
                </article>
              ))
            )}
            {hasHiddenCartLines ? (
              <Button type="default" block onClick={() => setShowAllCartLines((v) => !v)}>
                {showAllCartLines
                  ? t('showFewerItems', 'Show fewer items')
                  : t('showAllItems', `Show all items (${cartItems.length})`)}
              </Button>
            ) : null}
          </div>

          <div className="default-drawer-bottom">
            <div className="default-drawer-total">
              <div>
                <span>{t('totalItems', 'Total items')}</span>
                <strong>{cartItems.length}</strong>
              </div>
              <div>
                <span>{t('totalItemsQuantity', 'Total items quantity')}</span>
                <strong>{totalItemQuantity}</strong>
              </div>
              <div>
                <span>{t('subtotal', 'Subtotal')}</span>
                <Price amount={cart?.subtotal ?? 0} />
              </div>
              <div>
                <span>{t('tax', 'Tax')}</span>
                <Price amount={cart?.taxTotal ?? 0} />
              </div>
              {chargeRows.map((charge) => (
                <div key={charge.id}>
                  <span>{charge.label}</span>
                  <Price amount={charge.amount} />
                </div>
              ))}
              <div className="default-drawer-divider" />
              <div className="default-drawer-grand-total">
                <strong>{t('total', 'Total')}</strong>
                <Price amount={cart?.grandTotal ?? 0} size={22} />
              </div>
              <Button
                type="primary"
                size="large"
                block
                disabled={isCheckoutDisabled}
                onClick={openCheckout}
              >
                {t('proceedToCheckout', 'Proceed to Checkout')}
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
      <AppModal
        kind="product"
        open={Boolean(editingCartLine)}
        footer={null}
        onCancel={() => setEditingCartLine(null)}
        afterClose={releaseCartLine}
        width={isCartDrawerViewport ? 780 : 'calc(100% - 2rem)'}
        centered={!isCartDrawerViewport}
        destroyOnHidden
        zIndex={4100}
        className="default-product-modal product-detail-modal"
        styles={{ body: { padding: 0 }, content: { padding: 0, overflow: 'hidden' } }}
      >
        {shownCartLine ? (
          <ProductDetailView
            productId={shownCartLine.itemId}
            inModal
            cartLine={shownCartLine}
            onDone={() => setEditingCartLine(null)}
          />
        ) : null}
      </AppModal>
      <AppModal
        kind="checkout"
        open={checkoutOpen}
        footer={null}
        onCancel={() => setCheckoutOpen(false)}
        width={isCartDrawerViewport ? 1180 : '100%'}
        destroyOnHidden
        className="default-checkout-modal"
        styles={{ body: { padding: 0 }, content: { padding: 0, overflow: 'hidden' } }}
      >
        <Suspense fallback={<div className="default-checkout-loading">Loading checkout...</div>}>
          <CheckoutPage inModal />
        </Suspense>
      </AppModal>
    </>
  );
}
